import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import {
  type Asset,
  assetAnalysisStatusLabels,
  assetContentTypeLabels,
  useGetAssetAnalysis,
  useReanalyzeAsset,
} from '@/features/assets';
import {
  claimAnalysisRetry,
  releaseAnalysisRetry,
  useAnalysisTrackerStore,
} from '@/features/assets/background-analysis/analysis-tracker.store';
// Deep import: the assistant barrel reaches the router, and a gallery dialog
// that pulls the router in cannot be rendered on its own in a test.
import { createAssistantHandoff } from '@/features/assistant/lib/pending-handoff';
import { type Video, useGetVideo } from '@/features/videos';
import { useNavigate } from '@tanstack/react-router';
import { formatDistanceToNow } from 'date-fns';
import {
  Check,
  Clock,
  Download,
  Image as ImageIcon,
  Loader2,
  RefreshCw,
  SendIcon,
  Sparkles,
  Trash2,
  Video as VideoIcon,
  X,
} from 'lucide-react';
import { useState } from 'react';
import {
  attachableUrls,
  attachmentBaseName,
  fetchGalleryAttachments,
} from './edit-in-claire';
import { PostContentDialog } from './post-content-dialog';

// ─── Discriminated union for the two content types ───

type ContentItem =
  | { kind: 'asset'; data: Asset }
  | { kind: 'video'; data: Video };

interface ContentPreviewDialogProps {
  item: ContentItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDelete?: (item: ContentItem) => void;
  isDeleting?: boolean;
}

// ─── Helpers ───

function AnalysisStatusBadge({
  status,
}: {
  status: 'queued' | 'processing' | 'completed' | 'failed' | null;
}) {
  if (!status) {
    return (
      <Badge variant="outline" className="gap-1">
        <Clock className="size-3" />
        Not analyzed
      </Badge>
    );
  }

  const variants: Record<
    string,
    'default' | 'secondary' | 'destructive' | 'outline'
  > = {
    queued: 'outline',
    processing: 'secondary',
    completed: 'default',
    failed: 'destructive',
  };

  const icons: Record<string, React.ReactNode> = {
    queued: <Clock className="size-3" />,
    processing: <Loader2 className="size-3 animate-spin" />,
    completed: <Check className="size-3" />,
    failed: <X className="size-3" />,
  };

  return (
    <Badge variant={variants[status]} className="gap-1">
      {icons[status]}
      {assetAnalysisStatusLabels[status]}
    </Badge>
  );
}

// ─── Asset detail section ───

function AssetDetails({ asset }: { asset: Asset }) {
  const { analysis, linkedServices, isLoading, refetch } = useGetAssetAnalysis(
    asset.id
  );
  const { reanalyzeAssetAsync, isReanalyzing } = useReanalyzeAsset();

  const retryAnalysis = async () => {
    if (!claimAnalysisRetry(asset.id)) return;
    try {
      await reanalyzeAssetAsync(asset.id);
      const tracker = useAnalysisTrackerStore.getState();
      tracker.untrack([asset.id]);
      tracker.track([
        { assetId: asset.id, label: asset.sourceFileName ?? asset.name },
      ]);
    } catch {
      return;
    } finally {
      releaseAnalysisRetry(asset.id);
    }
  };

  return (
    <>
      <Separator />

      {/* Analysis Status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Analysis Status</span>
          <AnalysisStatusBadge status={analysis?.status ?? null} />
        </div>
        {analysis?.status !== 'processing' && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (analysis?.status === 'failed') void retryAnalysis();
              else void refetch();
            }}
            disabled={isLoading || isReanalyzing}
          >
            <RefreshCw
              className={`size-4 ${isLoading || isReanalyzing ? 'animate-spin' : ''}`}
            />
            {analysis?.status === 'failed' ? 'Retry' : 'Refresh'}
          </Button>
        )}
      </div>

      {/* Content Type */}
      {analysis?.contentType && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Content Type</h4>
          <Badge variant="outline">
            {assetContentTypeLabels[
              analysis.contentType as keyof typeof assetContentTypeLabels
            ] ?? analysis.contentType}
          </Badge>
        </div>
      )}

      {/* Linked Services */}
      {linkedServices.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Linked Services</h4>
          <div className="flex flex-wrap gap-2">
            {linkedServices.map((service) => (
              <Badge key={service.id} variant="outline">
                {service.serviceName}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* File Info */}
      <div className="space-y-1 text-sm text-muted-foreground">
        <p>File: {asset.sourceFileName}</p>
        <p>Type: {asset.type}</p>
      </div>
    </>
  );
}

// ─── Video detail section ───

function VideoDetails({ video }: { video: Video }) {
  return (
    <>
      <Separator />

      <div className="space-y-1 text-sm text-muted-foreground">
        <p>
          Created{' '}
          {formatDistanceToNow(new Date(video.createdAt), { addSuffix: true })}
        </p>
        {video.durationMs && (
          <p>Duration: {Math.round(Number(video.durationMs) / 1000)}s</p>
        )}
        {video.creator?.name && <p>Created by: {video.creator.name}</p>}
      </div>
    </>
  );
}

// ─── Main dialog ───

export function ContentPreviewDialog({
  item,
  open,
  onOpenChange,
  onDelete,
  isDeleting,
}: ContentPreviewDialogProps) {
  const [postDialogOpen, setPostDialogOpen] = useState(false);
  const [isOpeningChat, setIsOpeningChat] = useState(false);
  const navigate = useNavigate();

  // Fetch individual video to get presigned blobUrl (list endpoint nulls it out for perf)
  const { video: fullVideo } = useGetVideo(
    item?.kind === 'video' ? item.data.id : '',
    { enabled: open && item?.kind === 'video' && item.data.status === 'ready' }
  );

  if (!item) return null;

  const isAsset = item.kind === 'asset';
  const title = isAsset ? item.data.name : item.data.title || 'Untitled';
  const isVideo = isAsset ? item.data.type === 'video' : true; // AI-generated are always video
  // For AI-generated videos, prefer the individually-fetched video's blobUrl (presigned)
  const mediaUrl = isAsset
    ? item.data.blobUrl
    : (fullVideo?.blobUrl ?? item.data.blobUrl);
  const thumbnailUrl = !isAsset
    ? (fullVideo?.thumbnailUrl ?? item.data.thumbnailUrl)
    : null;
  const createdAt = isAsset ? item.data.createdAt : item.data.createdAt;
  const uploaderName = isAsset
    ? item.data.uploader?.name
    : item.data.creator?.name;

  /**
   * Open a chat with this video already sent — its poster frame as the
   * attachment (the assistant takes images only), its id as the active
   * context, which is what the editing tools address. Mirrors the merged
   * gallery's preview; see `edit-in-claire`.
   */
  const openInClaire = async () => {
    if (isAsset) return;
    setIsOpeningChat(true);
    try {
      const uploads = await fetchGalleryAttachments(
        attachableUrls({ kind: 'video', thumbnailUrl }),
        attachmentBaseName(title, 'video'),
        'video',
        item.data.id
      );
      const handoff = createAssistantHandoff({
        draft: 'Edit this video',
        uploads,
      });
      await navigate({
        to: '/assistant',
        search: {
          new: '1',
          handoff,
          entityType: 'video',
          entityId: item.data.id,
        },
      });
    } finally {
      setIsOpeningChat(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isVideo ? (
              <VideoIcon className="size-5" />
            ) : (
              <ImageIcon className="size-5" />
            )}
            {title}
          </DialogTitle>
          <DialogDescription>
            {new Date(createdAt).toLocaleDateString()}
            {uploaderName && ` \u00B7 ${uploaderName}`}
            {!isAsset && (
              <Badge variant="secondary" className="ml-2">
                AI Generated
              </Badge>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          {/* Preview */}
          <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-muted">
            {isVideo ? (
              mediaUrl ? (
                // biome-ignore lint/a11y/useMediaCaption: User content
                <video
                  src={mediaUrl}
                  className="h-full w-full object-contain"
                  controls
                />
              ) : thumbnailUrl ? (
                <img
                  src={thumbnailUrl}
                  alt={title}
                  className="h-full w-full object-contain"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center">
                  <VideoIcon className="size-12 text-muted-foreground" />
                </div>
              )
            ) : (
              <img
                src={mediaUrl ?? ''}
                alt={title}
                className="absolute inset-0 size-full object-contain"
              />
            )}
          </div>

          {/* Type-specific details */}
          {isAsset ? (
            <AssetDetails asset={item.data as Asset} />
          ) : (
            <VideoDetails video={item.data as Video} />
          )}
        </div>

        <DialogFooter className="gap-2">
          {/* Bottom-left: hand the generated video to Claire to change. Same
              entry point as the merged gallery's preview — it names the ASSET,
              and the chat API resolves it to the content item that owns it. */}
          {!isAsset && (
            <Button
              variant="outline"
              className="mr-auto"
              disabled={isOpeningChat}
              onClick={() => {
                void openInClaire();
              }}
            >
              <Sparkles className="size-4" />
              {isOpeningChat ? 'Opening...' : 'Edit Video'}
            </Button>
          )}
          {onDelete && (
            <Button
              variant="destructive"
              onClick={() => onDelete(item)}
              disabled={isDeleting}
            >
              <Trash2 className="size-4" />
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          )}
          {/* Download for ready AI videos */}
          {!isAsset && item.data.status === 'ready' && mediaUrl && (
            <Button
              variant="outline"
              onClick={() => window.open(mediaUrl, '_blank')}
            >
              <Download className="size-4" />
              Download
            </Button>
          )}
          {/* Post button for assets (they have blob URLs to post) */}
          {isAsset && (
            <Button onClick={() => setPostDialogOpen(true)}>
              <SendIcon className="size-4" />
              Post
            </Button>
          )}
        </DialogFooter>
      </DialogContent>

      {isAsset && (
        <PostContentDialog
          open={postDialogOpen}
          onOpenChange={setPostDialogOpen}
          asset={item.data as Asset}
          onSuccess={() => onOpenChange(false)}
        />
      )}
    </Dialog>
  );
}

export type { ContentItem };
